import React from "react";
// BASE Components
import BASELoading from "../../../../../../../@BASE/core/BASELoading";
import BASESvgIcon from "../../../../../../../@BASE/core/BASESvgIcon";
// Material UI
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
// Custom Components
import EMentor from "../components/EMentor";
import Instructions from "../components/Instructions";
import ComponentCard from "../components/Component";
import ComponentPreview from "../components/Preview";
import ComponentStepLabel from "../components/StepLabel";
import SubComponentCard from "../components/SubComponent";

export default function ErrorIdentification(props) {
  const { data, dir, ins = [], course, comments = [] } = props;
  const tl = `${dir === "rtl" ? "text-18" : "text-14"} ${course?.language?.acronym}`.trim();

  if (!data) {
    return <BASELoading />;
  }

  // filer comments by component_id
  const componentCommentsCount = (component) => {
    return comments.filter(
      (comment) =>
        comment.component === component && comment.internality !== true
    ).length;
  };

  return (
    <div>
      <style>
        {`
          .wysiwyg p {
            margin: 0px;
          }
        `}
      </style>
      <Typography className="text-16 font-bold p-16 pb-0">
        Page Instructions:
      </Typography>

      <Instructions
        ins={ins}
        course={course}
        dir={dir}
        tl={tl}
        componentCommentsCount={componentCommentsCount}
      />

      <Typography className="text-16 font-bold p-16 pb-0">
        Page Content:
      </Typography>

      {data.core.map((question, index) => (
        <Paper
          key={index}
          elevation={0}
          variant="outlined"
          square
          className="m-16"
        >
          <Typography className="text-15 font-bold px-16 pb-0">
            Question {index + 1}:
          </Typography>
          <Paper elevation={0} variant="outlined" square className="p-16 m-16">
            {question.materials.map((material, num) => (
              <Grid key={num} container spacing={0}>
                <Grid item xs={12}>
                  <Typography className="text-14 font-bold m-0">
                    Authentic{" "}
                    {material.media_material ? "Listening" : "Reading"}{" "}
                    Material:
                  </Typography>
                  <Divider className="my-16" />
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={0}>
                    <Grid item xs={12} sm={4} lg={2}>
                      <Typography className="text-14 font-semibold m-0">
                        Title (TL):
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} lg={10}>
                      <Typography className={`${tl} m-0`} dir={dir}>
                        {material.material_title_tl}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                      <Typography className="text-14 font-semibold m-0">
                        Title (EN):
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} lg={10}>
                      <Typography className="text-14 m-0">
                        {material.material_title_en}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                      <Typography className="text-14 font-semibold m-0">
                        Link:
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} lg={10}>
                      <Typography className="text-14 m-0">
                        {material.link}
                      </Typography>
                    </Grid>
                    {material.sp_url && (
                      <>
                        <Grid item xs={12} sm={4} lg={2}>
                          <Typography className="text-14 font-semibold m-0">
                            Sharepoint Source:
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={8} lg={10}>
                          <Button
                            variant="contained"
                            endIcon={
                              <BASESvgIcon size={20}>
                                heroicons-outline:external-link
                              </BASESvgIcon>
                            }
                            color="secondary"
                            size="small"
                            href={material.sp_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              bgcolor: "#013bb1 !important",
                              color: "#ffffff !important",
                              "&:hover": { bgcolor: "#001680 !important" },
                            }}
                          >
                            View Material
                          </Button>
                        </Grid>
                      </>
                    )}
                    {question.start_minute !== "" && (
                      <>
                        <Grid item xs={12} sm={4} lg={2}>
                          <Typography className="text-14 font-semibold m-0">
                            Time Stamp:
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={8} lg={10}>
                          <Typography className="text-14 m-0">
                            {question.start_minute < 10
                              ? `0${question.start_minute}`
                              : question.start_minute}
                            :
                            {question.start_second < 10
                              ? `0${question.start_second}`
                              : question.start_second}{" "}
                            -{" "}
                            {question.end_minute < 10
                              ? `0${question.end_minute}`
                              : question.end_minute}
                            :
                            {question.end_second < 10
                              ? `0${question.end_second}`
                              : question.end_second}{" "}
                            (trimmed from Authentic Material)
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Paper>

          <Paper elevation={0} variant="outlined" square className="p-16 m-16">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Grid container spacing={0}>
                  <Grid item xs={12}>
                    <Typography className="text-14 font-bold m-0">
                      Correct Authentic TL Transcribed Text {index + 1}:
                    </Typography>
                    <Divider className="my-16" />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      className={`${tl} m-0 wysiwyg`}
                      dir={dir}
                      dangerouslySetInnerHTML={{
                        __html: question.authentic_transcript_correct,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} variant="outlined" square className="p-16 m-16">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Grid container spacing={0}>
                  <Grid item xs={12}>
                    <Typography className="text-14 font-bold m-0">
                      Inaccurate Authentic TL Transcribed Text {index + 1}:
                    </Typography>
                    <Divider className="my-16" />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      className={`${tl} m-0 wysiwyg`}
                      dir={dir}
                      dangerouslySetInnerHTML={{
                        __html: question.authentic_transcript_inaccurate,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} variant="outlined" square className="p-16 m-16">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography className="text-14 font-bold m-0">
                  Lures:
                </Typography>
                <Divider className="my-16" />
                <Grid container spacing={0}>
                  {question.lures.map((lure, lIndex) => (
                    <Grid key={`lure-${lure.lure_text}-${lIndex}`} item xs={12}>
                      {lIndex > 0 && <Divider className="mt-2 mb-8" />}
                      <Grid container spacing={0} columns={24}>
                        <Grid item xs={4}>
                          <Typography className="text-14 font-semibold m-0">
                            Error {lIndex + 1}:
                          </Typography>
                        </Grid>
                        <Grid item xs>
                          <Typography
                            className={`${tl} m-0 ${
                              lIndex < question.lures.length - 1 && `mb-8`
                            }`}
                            dir={dir}
                          >
                            {lure.error}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid container spacing={0} columns={24}>
                        <Grid item xs={4}>
                          <Typography className="text-14 font-semibold m-0">
                            Feedback {lIndex + 1}:
                          </Typography>
                        </Grid>
                        <Grid item xs>
                          <Typography
                            className={`${tl} m-0 ${
                              lIndex < question.lures.length - 1 && `mb-8`
                            }`}
                            dir={dir}
                          >
                            {lure.feedback}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} variant="outlined" square className="p-16 m-16">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Grid container spacing={0}>
                  <Grid item xs={12}>
                    <Typography className="text-14 font-bold m-0">
                      Correct Feedback:
                    </Typography>
                    <Divider className="my-16" />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      className={`${tl} m-0 wysiwyg`}
                      dir={dir}
                      dangerouslySetInnerHTML={{
                        __html: question.correct_feedback,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} variant="outlined" square className="p-16 m-16">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Grid container spacing={0}>
                  <Grid item xs={12}>
                    <Typography className="text-14 font-bold m-0">
                      First Incorrect Feedback:
                    </Typography>
                    <Divider className="my-16" />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      className={`${tl} m-0 wysiwyg`}
                      dir={dir}
                      dangerouslySetInnerHTML={{
                        __html: question.incorrect_feedback_1,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} variant="outlined" square className="p-16 m-16">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Grid container spacing={0}>
                  <Grid item xs={12}>
                    <Typography className="text-14 font-bold m-0">
                      Second Incorrect Feedback:
                    </Typography>
                    <Divider className="my-16" />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      className={`${tl} m-0 wysiwyg`}
                      dir={dir}
                      dangerouslySetInnerHTML={{
                        __html: question.incorrect_feedback_2,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} variant="outlined" square className="p-16 m-16">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Grid container spacing={0}>
                  <Grid item xs={12}>
                    <Typography className="text-14 font-bold m-0">
                      Final Incorrect Feedback:
                    </Typography>
                    <Divider className="my-16" />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      className={`${tl} m-0 wysiwyg`}
                      dir={dir}
                      sx={{ margin: "0" }}
                      dangerouslySetInnerHTML={{
                        __html: question.incorrect_feedback_3,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} variant="outlined" square className="p-16 m-16">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography className="text-14 font-bold m-0">
                  Question Options:
                </Typography>
                <Divider className="my-16" />
                <Grid container spacing={1}>
                  <Grid item xs={4} sm={3}>
                    <Typography className="text-14 font-semibold m-0">
                      Maximum Number of Tries:
                    </Typography>
                  </Grid>
                  <Grid item xs={8} sm={9}>
                    <Typography className="text-14 m-0">
                      {question.max_number_of_tries}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Paper>
      ))}
      {data.eMentor.length !== 0 && (
        <EMentor
          eMentor={data.eMentor}
          tl={tl}
          dir={dir}
          componentCommentsCount={componentCommentsCount}
        />
      )}
    </div>
  );
}
